import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Heart, ShoppingCart, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';

const WishlistPage = () => {
  const [wishlist, setWishlist] = useState([]);
  const { addToCart } = useCart();

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('ppc_wishlist') || '[]');
    setWishlist(stored);
  }, []);

  const removeFromWishlist = (productId) => {
    const updated = wishlist.filter(p => p.id !== productId);
    setWishlist(updated);
    localStorage.setItem('ppc_wishlist', JSON.stringify(updated));
    toast.success('Removed from wishlist');
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success('Added to cart');
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>My Wishlist | The Perfect Peach Club</title>
      </Helmet>
      <Navigation />
      <ChatWidget />

      <div className="max-w-5xl mx-auto px-5 md:px-6 pt-24 md:pt-32 pb-12 md:pb-20">
        <div className="text-center mb-8">
          <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-[#2D2420] mb-3" data-testid="wishlist-heading">
            My Wishlist
          </h1>
          <p className="text-african-earth text-base md:text-lg">
            {wishlist.length > 0 ? `${wishlist.length} item${wishlist.length !== 1 ? 's' : ''} saved` : 'Your wishlist is empty'}
          </p>
        </div>

        {wishlist.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="w-16 h-16 text-[#E0D5D0] mx-auto mb-4" />
            <p className="text-african-earth mb-6">Save products you love by tapping the heart icon</p>
            <Link
              to="/products"
              className="inline-block bg-perfect-peach text-white px-8 py-3 rounded-full font-medium hover:bg-[#C57D63] transition-all duration-300"
              data-testid="wishlist-browse-link"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="wishlist-grid">
            {wishlist.map((product) => (
              <div key={product.id} className="bg-white rounded-2xl overflow-hidden shadow-soft group" data-testid={`wishlist-item-${product.id}`}>
                <div className="relative">
                  <img src={product.image_url} alt={product.name} className="w-full h-52 object-cover" />
                  <button
                    onClick={() => removeFromWishlist(product.id)}
                    className="absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-red-50 transition-colors"
                    data-testid={`remove-wishlist-${product.id}`}
                  >
                    <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                  </button>
                </div>
                <div className="p-4">
                  <h3 className="font-playfair text-lg font-semibold text-[#2D2420] mb-1">{product.name}</h3>
                  <p className="text-perfect-peach font-bold text-lg mb-3">R{product.price?.toFixed(2)}</p>
                  <button
                    onClick={() => handleAddToCart(product)}
                    className="w-full bg-perfect-peach text-white py-2.5 rounded-xl font-medium hover:bg-[#C57D63] transition-all duration-300 flex items-center justify-center gap-2 text-sm"
                    data-testid={`wishlist-add-cart-${product.id}`}
                  >
                    <ShoppingCart className="w-4 h-4" /> Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default WishlistPage;
